import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import {
    Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from "./ui/dialog";
import { Button } from "./ui/button";
import { Checkbox } from "./ui/checkbox";
import { api, formatApiError } from "../lib/api";

// Iter-158.13.2.20f.42 — Episode-selective + per-finding-selective
// AI Breakdown import drawer. Talks to the shared
// `POST .../hm-breakdowns/{bid}/import-to-entries` endpoint.
// Never mutates the immutable AI breakdown itself.

export default function HMImportToBreakdownDialog({
    open, onOpenChange,
    apiBase, projectId, breakdown, onImported,
}) {
    const [selectedFindings, setSelectedFindings] = useState({});
    const [selectedEpisodes, setSelectedEpisodes] = useState({});
    const [includeScriptWide, setIncludeScriptWide] = useState(false);
    const [busy, setBusy] = useState(false);

    // Build the finding catalogue from the breakdown's scene_view so
    // the ordering matches what the H&M user just read. Each finding
    // is keyed by `${section_index}:${finding_index}`; each ref
    // carries the finding's containing episode(s) so the checkbox
    // panel can be split by episode.
    const catalogue = useMemo(() => {
        const findingsByKey = new Map();
        const episodes = new Set();
        const scriptWideKeys = new Set();
        const sections = ((breakdown || {}).payload || {}).sections || [];
        sections.forEach((section, si) => {
            (section.findings || []).forEach((f, fi) => {
                const key = `${si}:${fi}`;
                const refEpisodes = new Set();
                (f.refs || []).forEach((r) => {
                    const ep = (r?.episode || "").trim();
                    if (ep) refEpisodes.add(ep);
                });
                refEpisodes.forEach((ep) => episodes.add(ep));
                const isScriptWide = !!f.script_wide && refEpisodes.size === 0;
                if (isScriptWide) scriptWideKeys.add(key);
                findingsByKey.set(key, {
                    key, section_index: si, finding_index: fi,
                    section_title: section.title || "",
                    kind: f.kind, facet: f.facet, note: f.note,
                    character_names: Array.from(new Set(
                        (f.refs || []).map((r) => r?.character_name).filter(Boolean),
                    )),
                    episodes: Array.from(refEpisodes).sort(),
                    is_script_wide: isScriptWide,
                    has_unresolved_scene: (f.refs || []).some(
                        (r) => (r?.scene_number || "").trim() && !r?.scene_id,
                    ),
                });
            });
        });
        return {
            findings: Array.from(findingsByKey.values()),
            episodes: Array.from(episodes).sort(),
            scriptWideKeys,
        };
    }, [breakdown]);

    // Pre-select everything the first time the dialog opens so the
    // fast path is "just press Add". The user can then de-select.
    useEffect(() => {
        if (!open) return;
        const findingDefaults = {};
        catalogue.findings.forEach((f) => { findingDefaults[f.key] = true; });
        const epDefaults = {};
        catalogue.episodes.forEach((ep) => { epDefaults[ep] = true; });
        setSelectedFindings(findingDefaults);
        setSelectedEpisodes(epDefaults);
        setIncludeScriptWide(catalogue.scriptWideKeys.size > 0);
    }, [open, catalogue]);

    const anyEpisodeSelected = Object.values(selectedEpisodes).some(Boolean);
    const selectedFindingCount = Object.values(selectedFindings).filter(Boolean).length;

    const doImport = async () => {
        if (!breakdown?.id || busy) return;
        setBusy(true);
        try {
            const episodeSelection = Object.entries(selectedEpisodes)
                .filter(([, v]) => v)
                .map(([k]) => k);
            const selected = Object.entries(selectedFindings)
                .filter(([, v]) => v)
                .map(([k]) => {
                    const [si, fi] = k.split(":");
                    return { section_index: Number(si), finding_index: Number(fi) };
                });
            const { data } = await api.post(
                `${apiBase}/${projectId}/hm-breakdowns/${breakdown.id}/import-to-entries`,
                {
                    episode_selection: episodeSelection,
                    selected,
                    include_script_wide: includeScriptWide,
                },
            );
            const parts = [];
            if (data.created) parts.push(`${data.created} added`);
            if (data.skipped_duplicates) parts.push(`${data.skipped_duplicates} already imported`);
            if (data.skipped_by_episode) parts.push(`${data.skipped_by_episode} skipped by episode`);
            if (data.skipped_script_wide) parts.push(`${data.skipped_script_wide} script-wide skipped`);
            toast.success(parts.length ? parts.join(" · ") : "Nothing to import");
            if (onImported) onImported(data);
            onOpenChange(false);
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setBusy(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent
                data-testid="hm-import-dialog"
                className="max-w-2xl max-h-[80vh] overflow-y-auto"
            >
                <DialogHeader>
                    <DialogTitle>Add to Scenes & Cast</DialogTitle>
                </DialogHeader>

                <div className="space-y-4">
                    <div>
                        <div className="text-[11px] uppercase tracking-[0.2em] text-neutral-400 mb-2">
                            Episodes
                        </div>
                        <div className="flex flex-wrap gap-2">
                            {catalogue.episodes.length === 0 ? (
                                <div className="text-[12px] text-neutral-500">
                                    No episode-scoped findings in this breakdown.
                                </div>
                            ) : catalogue.episodes.map((ep) => (
                                <label
                                    key={ep}
                                    data-testid={`hm-import-episode-${ep}`}
                                    className="inline-flex items-center gap-2 rounded-md border border-white/10 px-2 py-1 text-[12px] cursor-pointer hover:bg-white/5"
                                >
                                    <Checkbox
                                        checked={!!selectedEpisodes[ep]}
                                        onCheckedChange={(v) => setSelectedEpisodes((s) => ({ ...s, [ep]: !!v }))}
                                    />
                                    Episode {ep}
                                </label>
                            ))}
                        </div>
                    </div>

                    {catalogue.scriptWideKeys.size > 0 ? (
                        <label
                            data-testid="hm-import-scriptwide"
                            className="inline-flex items-center gap-2 rounded-md border border-violet-400/30 bg-violet-500/[0.06] px-2 py-1 text-[12px] cursor-pointer hover:bg-violet-500/10"
                        >
                            <Checkbox
                                checked={includeScriptWide}
                                onCheckedChange={(v) => setIncludeScriptWide(!!v)}
                            />
                            Include script-wide findings
                        </label>
                    ) : null}

                    <div>
                        <div className="text-[11px] uppercase tracking-[0.2em] text-neutral-400 mb-2">
                            Findings ({selectedFindingCount} / {catalogue.findings.length} selected)
                        </div>
                        <ul className="space-y-1.5 max-h-72 overflow-y-auto pr-1">
                            {catalogue.findings.map((f) => {
                                const isSw = f.is_script_wide;
                                return (
                                    <li
                                        key={f.key}
                                        data-testid={`hm-import-finding-${f.section_index}-${f.finding_index}`}
                                        className="flex items-start gap-2 rounded-md border border-white/5 p-2 text-[12px] text-neutral-200"
                                    >
                                        <Checkbox
                                            checked={!!selectedFindings[f.key]}
                                            onCheckedChange={(v) => setSelectedFindings((s) => ({ ...s, [f.key]: !!v }))}
                                        />
                                        <div className="min-w-0 flex-1">
                                            <div className="flex flex-wrap items-center gap-1 mb-0.5">
                                                {f.character_names.length ? (
                                                    <span className="text-[11px] text-white font-semibold">
                                                        {f.character_names.join(" / ")}
                                                    </span>
                                                ) : null}
                                                <span className="text-[10px] text-neutral-400">
                                                    {f.facet}
                                                </span>
                                                {isSw ? (
                                                    <span className="text-[9px] uppercase tracking-[0.15em] px-1.5 py-0.5 rounded-full border border-violet-400/40 bg-violet-500/10 text-violet-200">
                                                        Script-wide
                                                    </span>
                                                ) : f.episodes.length ? (
                                                    <span className="text-[9px] uppercase tracking-[0.15em] text-neutral-500">
                                                        Ep {f.episodes.join(", ")}
                                                    </span>
                                                ) : null}
                                                {f.has_unresolved_scene ? (
                                                    <span
                                                        data-testid={`hm-import-needs-review-${f.section_index}-${f.finding_index}`}
                                                        className="text-[9px] uppercase tracking-[0.15em] px-1.5 py-0.5 rounded-full border border-amber-400/40 bg-amber-500/10 text-amber-200"
                                                    >
                                                        Needs Review
                                                    </span>
                                                ) : null}
                                            </div>
                                            <div className="text-neutral-300 whitespace-pre-wrap line-clamp-3">
                                                {f.note}
                                            </div>
                                        </div>
                                    </li>
                                );
                            })}
                        </ul>
                    </div>
                </div>

                <DialogFooter>
                    <Button
                        variant="ghost"
                        onClick={() => onOpenChange(false)}
                        disabled={busy}
                        data-testid="hm-import-cancel"
                    >
                        Cancel
                    </Button>
                    <Button
                        onClick={doImport}
                        disabled={busy || selectedFindingCount === 0 || (!anyEpisodeSelected && !includeScriptWide)}
                        data-testid="hm-import-submit"
                    >
                        {busy ? "Adding…" : `Add ${selectedFindingCount} finding${selectedFindingCount === 1 ? "" : "s"}`}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
